import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  groupOf,
  pathOf,
  type Section,
  type SectionNode,
  resourceOf,
  sectionDestinations,
  sectionOf,
  sectionRowData,
} from '@/entities/section';
import { Icons, MenuItem, MenuList, MenuRow, Popover } from '@/shared/ui';

import styles from './SectionTree.module.css';

type Zone = 'into' | 'above' | 'below';

/** What a drag may do to a section row: file INTO it, or (sections only) reorder above / below. */
function zoneOf(element: HTMLElement, clientY: number, sectionDrag: boolean): Zone {
  if (!sectionDrag) return 'into';
  const rect = element.getBoundingClientRect();
  const share = (clientY - rect.top) / Math.max(rect.height, 1);
  return share < 0.28 ? 'above' : share > 0.72 ? 'below' : 'into';
}

export interface TreeActions {
  /** A resource was dropped onto a section (`null` = the workspace root): file it there. */
  onDropResource: (
    kind: 'board' | 'database' | 'document',
    id: string,
    sectionId: string | null,
  ) => void;
  /** A group was dropped onto a section (`null` = the root): it moves with what is inside. */
  onDropGroup: (id: string, sectionId: string | null) => void;
  /** A section was dropped: into `parentId` (end), or beside a sibling of that parent. */
  onDropSection: (
    id: string,
    parentId: string | null,
    anchor?: { after_id: string } | { before_id: string },
  ) => void;
  onFocus: (id: string) => void;
  onToggle: (id: string) => void;
  onCreateSub: (parent: Section) => void;
  onRename: (section: Section) => void;
  onMoveSection: (section: Section) => void;
  onDelete: (section: Section) => void;
}

interface Shared extends TreeActions {
  workspaceId: string;
  all: readonly Section[];
  maxDepth: number;
  canManage: boolean;
  collapsed: ReadonlySet<string>;
  /** Boards and databases of one container, already built (tiles with their own menus). */
  resources: (containerId: string | null) => ReactNode[];
  dragging: boolean;
}

function SectionHeader({
  node,
  open,
  focused,
  shared,
}: {
  node: SectionNode;
  open: boolean;
  focused: boolean;
  shared: Shared;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const [zone, setZone] = useState<Zone | 'blocked' | null>(null);
  const [dragging, setDragging] = useState(false);
  const { workspaceId, all, maxDepth, canManage, onDropResource, onDropSection, onToggle } = shared;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const clear = () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    };
    const verdict = (data: Record<string | symbol, unknown>, input: { clientY: number }) => {
      const dropped = resourceOf(data) ?? groupOf(data);
      if (dropped) return dropped.workspaceId === workspaceId ? ('into' as const) : null;
      const moving = sectionOf(data);
      if (!moving || moving.workspaceId !== workspaceId || moving.id === node.id) return null;
      const where = zoneOf(element, input.clientY, true);
      const allowed = sectionDestinations(all, moving.id, maxDepth);
      const parent = where === 'into' ? node.id : node.parent_id;
      return allowed.has(parent) ? where : ('blocked' as const);
    };
    return combine(
      ...(canManage
        ? [
            draggable({
              element,
              getInitialData: () => sectionRowData(workspaceId, node.id),
              onDragStart: () => {
                setDragging(true);
              },
              onDrop: () => {
                setDragging(false);
              },
            }),
          ]
        : []),
      dropTargetForElements({
        element,
        canDrop: ({ source, input }) => verdict(source.data, input) !== null,
        onDrag: ({ source, location }) => {
          setZone(verdict(source.data, location.current.input));
        },
        onDragEnter: ({ source, location }) => {
          setZone(verdict(source.data, location.current.input));
          // hovering a collapsed section opens it, so a nested destination can be reached
          if (!open && timer.current === null) {
            timer.current = window.setTimeout(() => {
              timer.current = null;
              onToggle(node.id);
            }, 650);
          }
        },
        onDragLeave: () => {
          setZone(null);
          clear();
        },
        onDrop: ({ source, location }) => {
          const result = verdict(source.data, location.current.input);
          setZone(null);
          clear();
          if (result === null || result === 'blocked') return;
          const dropped = resourceOf(source.data);
          if (dropped) {
            onDropResource(dropped.kind, dropped.id, node.id);
            return;
          }
          const group = groupOf(source.data);
          if (group) {
            shared.onDropGroup(group.id, node.id);
            return;
          }
          const moving = sectionOf(source.data);
          if (!moving) return;
          if (result === 'into') onDropSection(moving.id, node.id);
          else {
            onDropSection(
              moving.id,
              node.parent_id,
              result === 'above' ? { before_id: node.id } : { after_id: node.id },
            );
          }
        },
      }),
    );
  }, [
    all,
    canManage,
    maxDepth,
    node.id,
    node.parent_id,
    onDropResource,
    onDropSection,
    onToggle,
    open,
    shared,
    workspaceId,
  ]);

  return (
    <div
      ref={ref}
      className={styles.header}
      data-testid="section-header"
      data-section-id={node.id}
      data-drop={zone ?? undefined}
      data-dragging={dragging || undefined}
      data-focused={focused || undefined}
    >
      {canManage && (
        <span
          className={styles.grip}
          aria-hidden
          title={t('sections.dragSection', { name: node.name })}
        >
          <Icons.grip />
        </span>
      )}
      {!focused && (
        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={open}
          aria-label={t(open ? 'sections.collapse' : 'sections.expand', { name: node.name })}
          data-testid="section-toggle"
          onClick={() => {
            shared.onToggle(node.id);
          }}
        >
          <span className={styles.chevron} data-open={open || undefined} aria-hidden>
            <Icons.chevronRight />
          </span>
        </button>
      )}
      <button
        type="button"
        className={styles.name}
        title={pathOf(all, node.id)
          .map((s) => s.name)
          .join(' / ')}
        aria-label={t('sections.open', { name: node.name })}
        data-testid="section-open"
        onClick={() => {
          shared.onFocus(node.id);
        }}
      >
        <Icons.folder />
        <span className={styles.nameText}>{node.name}</span>
      </button>
      {canManage && (
        <Popover
          label={t('sections.menu', { name: node.name })}
          width={240}
          alignEnd
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className={styles.menu}
              aria-label={t('sections.menu', { name: node.name })}
              data-testid="section-menu"
            >
              <Icons.more />
            </button>
          )}
        >
          {(close) => (
            <MenuList>
              {node.depth < maxDepth && (
                <MenuRow>
                  <MenuItem
                    icon={<Icons.plus />}
                    onClick={() => {
                      close();
                      shared.onCreateSub(node);
                    }}
                  >
                    {t('sections.newSubsection')}
                  </MenuItem>
                </MenuRow>
              )}
              <MenuRow>
                <MenuItem
                  icon={<Icons.edit />}
                  onClick={() => {
                    close();
                    shared.onRename(node);
                  }}
                >
                  {t('sections.rename')}
                </MenuItem>
              </MenuRow>
              <MenuRow>
                <MenuItem
                  icon={<Icons.folder />}
                  onClick={() => {
                    close();
                    shared.onMoveSection(node);
                  }}
                >
                  {t('sections.moveSection')}
                </MenuItem>
              </MenuRow>
              <MenuRow separated>
                <MenuItem
                  icon={<Icons.trash />}
                  danger
                  onClick={() => {
                    close();
                    shared.onDelete(node);
                  }}
                >
                  {t('sections.delete')}
                </MenuItem>
              </MenuRow>
            </MenuList>
          )}
        </Popover>
      )}
    </div>
  );
}

/** One section: its header, then (when open) its child sections and its boards and databases. */
function Branch({
  node,
  shared,
  focused = false,
}: {
  node: SectionNode;
  shared: Shared;
  focused?: boolean;
}) {
  const { t } = useTranslation();
  const open = focused || !shared.collapsed.has(node.id);
  const items = open ? shared.resources(node.id) : [];
  const bodyId = `section-body-${node.id}`;
  return (
    <li
      className={styles.branch}
      data-testid="section-branch"
      style={{ '--depth': Math.min(node.depth - 1, 3) } as CSSProperties}
    >
      <SectionHeader node={node} open={open} focused={focused} shared={shared} />
      {open && (
        <div id={bodyId} className={styles.body}>
          {node.children.length > 0 && (
            <ul className={styles.branches} aria-label={t('sections.title')}>
              {node.children.map((child) => (
                <Branch key={child.id} node={child} shared={shared} />
              ))}
            </ul>
          )}
          {items.length > 0 && (
            <ul className={styles.grid} data-testid="section-resources">
              {items}
            </ul>
          )}
          {node.children.length === 0 && items.length === 0 && (
            <p className={styles.empty} data-testid="section-empty">
              {t('sections.empty')}{' '}
              <span className={styles.emptyHint}>
                {shared.canManage ? t('sections.emptyHint') : ''}
              </span>
            </p>
          )}
        </div>
      )}
    </li>
  );
}

/** A drop zone for the workspace root, shown only while something is being dragged. */
function RootDrop({ shared }: { shared: Shared }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(false);
  const { workspaceId, onDropResource, onDropSection } = shared;
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        const dropped = resourceOf(source.data) ?? sectionOf(source.data) ?? groupOf(source.data);
        return dropped !== null && dropped.workspaceId === workspaceId;
      },
      onDragEnter: () => {
        setOver(true);
      },
      onDragLeave: () => {
        setOver(false);
      },
      onDrop: ({ source }) => {
        setOver(false);
        const dropped = resourceOf(source.data);
        if (dropped) onDropResource(dropped.kind, dropped.id, null);
        else {
          const moving = sectionOf(source.data);
          if (moving) onDropSection(moving.id, null);
          const group = groupOf(source.data);
          if (group) shared.onDropGroup(group.id, null);
        }
      },
    });
  }, [workspaceId, onDropResource, onDropSection, shared]);
  return (
    <div
      ref={ref}
      className={styles.rootDrop}
      data-over={over || undefined}
      data-testid="root-drop"
    >
      <Icons.workspace /> {t('sections.dropRoot')}
    </div>
  );
}

/**
 * The section hierarchy of a workspace catalog: sections as collapsible branches (disclosure
 * buttons, `aria-expanded`), each holding its child sections, then its boards and databases (the
 * order of a container is: sections, boards, databases - each in its own manual order). Rows are
 * dragged (grip) to reorder or re-parent; boards and databases are filed by dropping them on a
 * row. The same moves are available as menu actions: nothing needs a mouse.
 */
export function SectionTree({
  roots,
  focus,
  shared,
}: {
  roots: readonly SectionNode[];
  /** Show only this section (its subtree) - the breadcrumb view. */
  focus: SectionNode | null;
  shared: Omit<Shared, 'dragging'>;
}) {
  const [dragging, setDragging] = useState(false);
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) =>
          resourceOf(source.data) !== null ||
          sectionOf(source.data) !== null ||
          groupOf(source.data) !== null,
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
    [],
  );
  const all: Shared = { ...shared, dragging };
  const rootItems = focus ? [] : shared.resources(null);
  return (
    <div className={styles.tree} data-testid="section-tree">
      {focus ? (
        <ul className={styles.branches}>
          <Branch node={focus} shared={all} focused />
        </ul>
      ) : (
        <>
          {roots.length > 0 && (
            <ul className={styles.branches}>
              {roots.map((node) => (
                <Branch key={node.id} node={node} shared={all} />
              ))}
            </ul>
          )}
          {rootItems.length > 0 && (
            <ul className={styles.grid} data-testid="workspace-objects">
              {rootItems}
            </ul>
          )}
        </>
      )}
      {dragging && <RootDrop shared={all} />}
    </div>
  );
}
